import { useState } from 'react';
import {
  Alert,
  Button,
  Center,
  Group,
  Paper,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconMail } from '@tabler/icons-react';
import { API_WORKER_URL, venueSlugFromBrowserHost } from '@shared-endpoints';

/**
 * Staff login — request a magic-link via email.
 *
 * We POST to the shared Worker (`/api/auth/request-link`) with the
 * `x-ubc-venue-slug` header so the worker knows which venue's staff
 * roster to check. Response is always 200; UI shows a generic
 * "check your email" message regardless of whether the email exists
 * in the roster (no information leak).
 *
 * Click the emailed link → worker verifies → sets session cookie →
 * 302 redirects back to console → StaffProfileProvider sees the
 * session and renders the app normally.
 */
export function LoginPage(): JSX.Element {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (email.trim().length === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      const apiBase =
        import.meta.env.VITE_API_BASE_URL ??
        (import.meta.env.PROD ? API_WORKER_URL : '');
      const slug = venueSlugFromBrowserHost({
        devFallback: import.meta.env.VITE_DEV_VENUE_SLUG,
      });
      const res = await fetch(`${apiBase}/api/auth/request-link`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          ...(slug ? { 'x-ubc-venue-slug': slug } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({ email: email.trim() }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Center style={{ minHeight: '100vh' }} p="md">
      <Paper withBorder radius="lg" p="xl" style={{ maxWidth: 420, width: '100%' }}>
        <Stack gap="md">
          <Title order={3}>店家後台登入</Title>

          {sent ? (
            <Alert color="teal" icon={<IconMail size={16} />}>
              若此 email 是本店員工，我們已寄出登入連結。請查收信箱（15
              分鐘內有效、只能點一次）。
            </Alert>
          ) : (
            <>
              <Text size="sm" c="dimmed">
                請輸入員工 email，我們會寄登入連結給您。
              </Text>
              <form onSubmit={(e) => void handleSubmit(e)}>
                <Stack gap="sm">
                  <TextInput
                    type="email"
                    required
                    autoFocus
                    label="員工 Email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.currentTarget.value)}
                    disabled={submitting}
                  />
                  {error && (
                    <Alert color="red" variant="light">
                      {error}
                    </Alert>
                  )}
                  <Group justify="flex-end">
                    <Button type="submit" loading={submitting}>
                      寄登入連結
                    </Button>
                  </Group>
                </Stack>
              </form>
            </>
          )}
        </Stack>
      </Paper>
    </Center>
  );
}
